/* eslint-disable no-nested-ternary */
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { getRouteApi } from '@tanstack/react-router';
import { useMemo, useState, useCallback } from 'react';
import GoBackHeader from '@emfood/shared/src/components/go-back-header/go-back-header';
import {
  fromUtcDay,
  useAuthContext,
  SearchTextField,
  createSortSchema,
  ACHelmetContainer,
  createPaginationSchema,
  useSyncMRTSearchParams,
  getMRTModelsFromSearchParams,
} from '@emfood/shared';

import { DatePicker } from '@mui/x-date-pickers';
import { Box, Card, Stack, Button, useTheme, Typography, useMediaQuery } from '@mui/material';

import { SearchAndFilterData, useCalculatedProductList } from 'src/hooks/use-product-list';

import { FilterIcon } from 'src/assets/icons';
import { Item, Basket } from 'src/api/models/cart';
import {
  useAddToCart,
  useRemoveFromCart,
  useUpdateProductQuantity,
  useGetBuyerSiteLinkedSites,
} from 'src/api/hooks/api-hooks';

import { useSettingsContext } from 'src/components/settings';

import MobileProductList from './product-list-mobile';
import ProductListMRTTable from './product-list-mrt-table';
import ProductActiveFilters from './product-active-filters';
import ProductFilterSidebar from './product-filter-sidebar';
import { ActiveOperations, ActiveOperationType } from './action-cell';
import { FavouritesCollectionOption } from './favourites-multiselect';

// --- Search params schema ---

const ProductsListFilterSchema = Yup.object({
  productName: Yup.string().optional(),
  availableFrom: Yup.string().optional(),
  producerDistance: Yup.string().optional(),
  distributorSiteId: Yup.string().optional(),
  allergens: Yup.array().of(Yup.string().required()).optional(),
  categories: Yup.array().of(Yup.string().required()).optional(),
  certifications: Yup.array().of(Yup.string().required()).optional(),
  dietaryLifestyles: Yup.array().of(Yup.string().required()).optional(),
  countryOfOrigin: Yup.array().of(Yup.string().required()).optional(),
  productType: Yup.string().optional(),
  airfreighted: Yup.boolean().optional(),
  favouriteCollectionIds: Yup.array().of(Yup.string().required()).optional(),
});

export const ProductsListSearchParamsSchema = ProductsListFilterSchema.concat(
  createPaginationSchema({ allowedPageSizes: [25] })
).concat(
  createSortSchema(
    ['name', 'price', 'pricePerStandardUnit', 'productName', 'earliestDeliveryDate'] as const,
    { defaultSortBy: 'productName', defaultSortOrder: 'asc' }
  )
);

// --- Helpers ---

function toSearchAndFilterData(fm: Record<string, any>): SearchAndFilterData {
  return {
    productName: (fm.productName as string) ?? '',
    availableFrom: fromUtcDay(fm.availableFrom as string),
    producerDistance: (fm.producerDistance as string) ?? '',
    distributorSiteId: (fm.distributorSiteId as string) ?? '',
    allergens: (fm.allergens as string[]) ?? [],
    categories: (fm.categories as string[]) ?? [],
    certifications: (fm.certifications as string[]) ?? [],
    dietaryLifestyles: (fm.dietaryLifestyles as string[]) ?? [],
    countryOfOrigin: (fm.countryOfOrigin as string[]) ?? [],
    productType: (fm.productType as string) ?? null,
    airfreighted: (fm.airfreighted as boolean) ?? undefined,
    favouriteCollectionIds: (fm.favouriteCollectionIds as string[]) ?? [],
  };
}

function toFilterModel(data: SearchAndFilterData): Record<string, any> {
  const result: Record<string, any> = {};
  if (data.productName) result.productName = data.productName;
  if (data.availableFrom) result.availableFrom = data.availableFrom;
  if (data.producerDistance) result.producerDistance = data.producerDistance;
  if (data.distributorSiteId) result.distributorSiteId = data.distributorSiteId;
  if (data.allergens?.length) result.allergens = data.allergens;
  if (data.categories?.length) result.categories = data.categories;
  if (data.certifications?.length) result.certifications = data.certifications;
  if (data.dietaryLifestyles?.length) result.dietaryLifestyles = data.dietaryLifestyles;
  if (data.countryOfOrigin?.length) result.countryOfOrigin = data.countryOfOrigin;
  if (data.productType) result.productType = data.productType;
  if (data.airfreighted !== undefined) result.airfreighted = data.airfreighted;
  if (data.favouriteCollectionIds?.length)
    result.favouriteCollectionIds = data.favouriteCollectionIds;
  return result;
}

// --- Route API ---

const routeApi = getRouteApi('/products/');

// --- Component ---

export default function ProductsList() {
  const settings = useSettingsContext();
  const theme = useTheme();

  const navigate = routeApi.useNavigate();
  const routeSearch = routeApi.useSearch();

  const {
    paginationModel: searchPaginationModel,
    sortModel: searchSortModel,
    filterModel: searchFilterModel,
  } = getMRTModelsFromSearchParams(routeSearch);

  // Filter model state (URL-compatible shape, source of truth for applied filters)
  const [filterModel, setFilterModel] = useState<Record<string, any>>(() => {
    const fm = searchFilterModel ?? {};
    // Convert availableFrom string from URL to Date for consistent serialisation
    if (fm.availableFrom && typeof fm.availableFrom === 'string') {
      return { ...fm, availableFrom: fromUtcDay(fm.availableFrom) };
    }
    return fm;
  });

  // Derive SearchAndFilterData from filter model (for the hook & active-filters display)
  const filterSearchModel = useMemo<SearchAndFilterData>(
    () => toSearchAndFilterData(filterModel),
    [filterModel]
  );

  const defaultFilters: SearchAndFilterData = useMemo(
    () => ({
      productName: '',
      availableFrom: null,
      producerDistance: '',
      distributorSiteId: '',
      allergens: [],
      categories: [],
      certifications: [],
      dietaryLifestyles: [],
      countryOfOrigin: [],
      productType: null,
      airfreighted: undefined,
      favouriteCollectionIds: [],
    }),
    []
  );

  // Local editing state for sidebar / search fields (may differ from applied filters while editing)
  const [localFilters, setLocalFilters] = useState<SearchAndFilterData>(filterSearchModel);

  // --- Submit / remove filter callbacks ---

  const onSubmitFilters = useCallback((data: SearchAndFilterData) => {
    setLocalFilters(data);
    setFilterModel(toFilterModel(data));
  }, []);

  const onRemoveFilter = useCallback((field: keyof SearchAndFilterData, value?: string) => {
    // Update the applied filter model
    setFilterModel((prev) => {
      const updated = { ...prev };
      if (value) {
        const arr = (updated[field] as string[]) || [];
        const filtered = arr.filter((v) => v !== value);
        if (filtered.length > 0) {
          updated[field] = filtered;
        } else {
          delete updated[field];
        }
      } else {
        delete updated[field];
      }
      return updated;
    });

    // Keep localFilters in sync for the sidebar UI
    setLocalFilters((prev) => {
      const updated = { ...prev };
      if (value) {
        (updated[field] as string[]) = ((updated[field] as string[]) || []).filter(
          (item) => item !== value
        );
      } else if (field === 'productName' || field === 'producerDistance') {
        (updated as any)[field] = '';
      } else if (field === 'airfreighted') {
        updated.airfreighted = undefined;
      } else if (field === 'distributorSiteId') {
        updated.distributorSiteId = '';
      } else if (field === 'availableFrom' || field === 'productType') {
        (updated as any)[field] = null;
      } else {
        (updated as any)[field] = [];
      }
      return updated;
    });
  }, []);

  const onClearAllFilters = useCallback(() => {
    setFilterModel({});
    setLocalFilters(defaultFilters);
  }, [defaultFilters]);

  // --- Hook (pagination, sort & data fetching) ---

  const {
    products,
    productsTotalCount,
    productsTotalPages,
    fetchProductsLoading,
    paginationModel,
    sortModel,
    setSortModel,
    setPaginationModel,
    personalFavouritesCollections,
    favouritesCollectionsData,
    fetchloadingFavouritesCollections,
    fetchFavouritesCollectionsError,
    handleQuantityChange,
  } = useCalculatedProductList({
    initialPaginationModel: searchPaginationModel,
    initialSortModel: searchSortModel,
    filterSearchModel,
  });

  // Sync pagination, sort & filters to URL
  useSyncMRTSearchParams({
    navigate,
    paginationModel,
    sortModel,
    filterModel,
  });

  // Flattened favourites collections (personal + shared) for the filter control & chips
  const favouritesCollections = useMemo<FavouritesCollectionOption[]>(() => {
    if (!favouritesCollectionsData) return [];
    return [
      ...(favouritesCollectionsData.personal ?? []).map((collection) => ({
        id: collection.id,
        name: collection.name,
      })),
      ...(favouritesCollectionsData.orgShared ?? []).map((collection) => ({
        id: collection.id,
        name: collection.name,
        isShared: true,
      })),
      ...(favouritesCollectionsData.siteShared ?? []).map((collection) => ({
        id: collection.id,
        name: collection.name,
        isShared: true,
      })),
    ];
  }, [favouritesCollectionsData]);

  // --- Misc state ---

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const { mutate: addToCart } = useAddToCart();
  const { mutate: removeFromCart } = useRemoveFromCart();
  const { mutate: updateQuantity } = useUpdateProductQuantity();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Per-row in-flight mutation tracker. Multiple rows may be updating concurrently
  // (concurrent updates are safe because handleUpdate's setQueryData is a functional
  // updater that patches a single cartItem by id). Add/Remove replace the whole cart,
  // so action-cell's disable logic treats those as cart-replacing.
  const [activeOperations, setActiveOperations] = useState<ActiveOperations>(() => new Map());

  const markOperation = useCallback((rowId: string, type: ActiveOperationType) => {
    setActiveOperations((prev) => {
      const next = new Map(prev);
      next.set(rowId, type);
      return next;
    });
  }, []);

  const clearOperation = useCallback((rowId: string) => {
    setActiveOperations((prev) => {
      if (!prev.has(rowId)) return prev;
      const next = new Map(prev);
      next.delete(rowId);
      return next;
    });
  }, []);

  const handleAddToCart = useCallback(
    async (productStockId: string, quantity: number, rowId: string) => {
      markOperation(rowId, 'add');
      addToCart(
        {
          body: {
            productStockId,
            quantity,
          },
        },
        {
          onSuccess: async (response) => {
            enqueueSnackbar('Successfully added to basket');
            queryClient.setQueryData(['cart'], response);
            clearOperation(rowId);
          },
          onError: () => {
            clearOperation(rowId);
          },
        }
      );
    },
    [addToCart, clearOperation, enqueueSnackbar, markOperation, queryClient]
  );

  const handleRemoveFromCart = useCallback(
    (cartItemId: string, rowId: string) => {
      markOperation(rowId, 'remove');
      removeFromCart(
        {
          pathParams: {
            itemId: cartItemId,
          },
        },
        {
          onSuccess: (response) => {
            enqueueSnackbar('Removed from basket.');
            queryClient.setQueryData(['cart'], response);
            clearOperation(rowId);
          },
          onError: () => {
            clearOperation(rowId);
          },
        }
      );
    },
    [clearOperation, enqueueSnackbar, markOperation, queryClient, removeFromCart]
  );

  const handleUpdate = useCallback(
    (cartItemId: string, quantity: number, rowId: string) => {
      markOperation(rowId, 'update');
      updateQuantity(
        {
          body: {
            quantity,
          },
          pathParams: {
            itemId: cartItemId,
          },
        },
        {
          onSuccess: (responseQuantity) => {
            enqueueSnackbar('Successfully updated quantity');
            queryClient.setQueryData(['cart'], (oldData: any) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                baskets: oldData.baskets.map((basket: Basket) => ({
                  ...basket,
                  items: basket.items.map((item: Item) =>
                    item.id === cartItemId ? { ...item, quantity: responseQuantity } : item
                  ),
                })),
              };
            });
            clearOperation(rowId);
          },
          onError: () => {
            clearOperation(rowId);
          },
        }
      );
    },
    [clearOperation, enqueueSnackbar, markOperation, queryClient, updateQuantity]
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user } = useAuthContext();

  const { data: linkedSites } = useGetBuyerSiteLinkedSites(
    user?.buyerOrg?.id || '',
    user?.buyerSite?.id || '',
    true
  );

  const [openFilterSidebar, setOpenFilterSidebar] = useState(false);
  const [dateError, setDateError] = useState<string>('');

  return (
    <ACHelmetContainer title="Products" maxWidth={settings.themeStretch ? false : 'xl'}>
      <GoBackHeader heading="Product Search" disableGoBack />
      <Card sx={{ p: 3, mb: 3 }}>
        <Box width="100%">
          <Typography variant="h2" gutterBottom mb={3}>
            Search fields
          </Typography>
          <Stack
            component="fieldset"
            aria-label="Product Search"
            border={0}
            direction={{ sm: 'row', xs: 'column' }}
            gap={2}
            alignItems="flex-start"
          >
            <legend
              style={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                border: 0,
              }}
            >
              Search Products by available from date or by search term
            </legend>
            <DatePicker
              value={localFilters.availableFrom}
              sx={{
                width: { xs: '100%', sm: 300 },
                minWidth: 200,
                '& .MuiInputBase-root': {
                  color: 'grey.700',
                  '&:hover': {
                    color: 'grey.800',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'grey.700',
                  '&.Mui-focused': {
                    color: 'grey.800',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'grey.700',
                  '&:hover': {
                    borderColor: 'grey.800',
                  },
                  '&.Mui-focused': {
                    borderColor: 'grey.800',
                  },
                },
              }}
              label="Available on or before"
              format="dd MMMM yyyy"
              minDate={today}
              onChange={(newValue) => {
                if (newValue && newValue < today) {
                  setDateError('Date must be in the future');
                } else {
                  setDateError('');
                  setLocalFilters({ ...localFilters, availableFrom: newValue });
                  onSubmitFilters({ ...localFilters, availableFrom: newValue });
                }
              }}
              onError={(error) => {
                if (error === 'minDate') {
                  setDateError('Date must be in the future');
                } else if (error) {
                  setDateError('Invalid date');
                } else {
                  setDateError('');
                }
              }}
              slotProps={{
                popper: {
                  sx: {
                    zIndex: theme.zIndex.appBar - 1,
                  },
                },
                textField: {
                  error: !!dateError,
                  helperText: dateError,
                  FormHelperTextProps: {
                    role: 'alert',
                  },
                  inputProps: {
                    'aria-label': 'Available from Date in the format DD MMMM YYYY',
                  },
                },
              }}
            />
            <SearchTextField
              value={localFilters.productName}
              onChange={(e) => {
                setLocalFilters({ ...localFilters, productName: e.target.value });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSubmitFilters(localFilters);
                }
              }}
              onClickSearchButton={() => onSubmitFilters(localFilters)}
            />
          </Stack>
        </Box>
      </Card>
      <Card
        sx={{
          p: 3,
          '@media (max-width:1467px)': { px: 0 },
        }}
      >
        <Box width="100%" mb={3} component="section">
          <Stack
            direction="row"
            alignItems="flex-start"
            gap={2}
            sx={{
              mb: 2,
              // At the narrow breakpoint the wrapping Card drops horizontal padding;
              // restore it on this row so chips/button don't hug the edges.
              '@media (max-width:1467px)': { px: 3 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ProductActiveFilters
                filterSearchModel={filterSearchModel}
                onRemoveFilter={onRemoveFilter}
                onClearAllFilters={onClearAllFilters}
                linkedSites={linkedSites?.map((site) => ({ id: site.id, name: site.name })) || []}
                favouritesCollections={favouritesCollections}
              />
            </Box>
            <Button onClick={() => setOpenFilterSidebar(true)} endIcon={<FilterIcon />}>
              Filters
            </Button>
          </Stack>
        </Box>

        {isMobile && (
          <MobileProductList
            products={products || []}
            totalPages={productsTotalPages}
            paginationModel={paginationModel}
            onPaginationModelChanged={setPaginationModel}
            isLoading={fetchProductsLoading}
            personalFavouritesCollections={personalFavouritesCollections}
            isFavouritesLoading={fetchloadingFavouritesCollections}
            favouritesCollectionsError={fetchFavouritesCollectionsError}
            handleAddToCart={handleAddToCart}
            handleUpdate={handleUpdate}
            handleRemoveFromCart={handleRemoveFromCart}
            handleQuantityChange={handleQuantityChange}
          />
        )}
        {!isMobile && (
          <ProductListMRTTable
            products={products}
            isLoading={fetchProductsLoading}
            productsTotalCount={productsTotalCount}
            paginationModel={paginationModel}
            sortModel={sortModel}
            onSortChanged={setSortModel}
            onPaginationModelChanged={setPaginationModel}
            personalFavouritesCollections={personalFavouritesCollections}
            favouritesCollectionsData={favouritesCollectionsData}
            isFavouritesLoading={fetchloadingFavouritesCollections}
            favouritesCollectionsError={fetchFavouritesCollectionsError}
            handleAddToCart={handleAddToCart}
            handleUpdate={handleUpdate}
            handleRemoveFromCart={handleRemoveFromCart}
            handleQuantityChange={handleQuantityChange}
            activeOperations={activeOperations}
          />
        )}
      </Card>

      <ProductFilterSidebar
        open={openFilterSidebar}
        onClose={() => setOpenFilterSidebar(false)}
        onClearFilters={onClearAllFilters}
        onApplyFilters={onSubmitFilters}
        linkedSites={linkedSites?.map((site) => ({ id: site.id, name: site.name })) || []}
        favouritesCollections={favouritesCollections}
        isLoading={fetchProductsLoading}
        filterData={localFilters}
        onChange={(field, value) => {
          setLocalFilters((prev) => ({ ...prev, [field]: value }));
        }}
      />
    </ACHelmetContainer>
  );
}
